import { useElements, useStripe } from "@stripe/react-stripe-js";
import Button from "components/form/Button";
import { valuesProps } from "types/paymentMethods";
import { AddressPayload, SettingsValue } from "types/payments";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import {
  onSetFinalDisclosureLoading,
  onSetShowDisclosure,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import {
  duplicatePaymentGet,
  getIntent,
  markIncompletePayment,
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
  setIsPaymentProcessing,
} from "store/slices/paymentsSlice";
import { countryAddressList } from "utils/countryAddressList";
import { decode } from "utils/HelperFunctions";
import { StripeCardNumberElement } from "@stripe/stripe-js";
import { useTestAttrsFactory } from "utils/testid";
import { captureException } from "utils/sentry";
import {
  clearStoredCardElement,
  getClientSecret,
  getStoredCardElement,
  PAYMENT_CONFIRMATION_ERROR,
  PAYMENT_START_ERROR,
} from "utils/stripeIntent";
import {
  getIntentOrderId,
  getPaymentFailureReason,
} from "utils/incompleteOrder";

interface CardPaymentButtonProps {
  amount: string | number;
  values: valuesProps;
  email: string;
  buttonText: string;
  className?: string;
  isDisabled?: boolean;
  handleClick?: () => void;
  isRecurring?: boolean;
  setCardErrMsg?: (msg: string) => void;
  isUpdateAndPay?: boolean;
  orderId?: any;
  verifyStripeCardPayment: ({
    intent,
    email,
    error,
    orderId,
    updateMethod,
  }: {
    intent: string | null;
    email: string;
    error: any;
    orderId?: number;
    updateMethod: boolean;
  }) => Promise<void>;
}
const CardPaymentButton = forwardRef(
  (
    {
      amount,
      values,
      buttonText,
      email,
      className = "",
      isDisabled = false,
      isRecurring = false,
      handleClick = () => {},
      setCardErrMsg = () => {},
      verifyStripeCardPayment,
      isUpdateAndPay = false,
      orderId: retryOrderId,
    }: CardPaymentButtonProps,
    ref
  ) => {
    const testAttrs = useTestAttrsFactory();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const userData = useUserData();
    const { clusterId: cluster_id, id: encodedOrderId } = useParams();
    const clusterId = decode(cluster_id || "", true);
    const resolvedClusterId =
      clusterId ||
      Number(decode(encodedOrderId || "")?.split("_")[1]) ||
      userData?.data?.accountData?.clusterid;
    const effectiveRetryOrderId =
      retryOrderId || Number(decode(encodedOrderId || "")?.split("_")[0]) || 0;
    const dispatch = useAppDispatch();
    const { checkedDuplicatePayment } = useAppSelector(
      (state) => state.payment
    );
    const stripe = useStripe();
    const elements = useElements();

    const portalSettings = usePortalSettingsData();
    const showFinalDisclosure = useShowFinalDisclosure();

    const markIntentFailed = async (orderId: number | undefined, error: any) => {
      if (!orderId) return;
      try {
        await dispatch(
          markIncompletePayment({
            orderId,
            cluster_id: resolvedClusterId || undefined,
            failed_reason: getPaymentFailureReason(error),
          })
        ).unwrap();
      } catch (markError) {
        captureException(markError, {
          tags: { payment_flow: "mark_incomplete_failed" },
          extra: { order_id: orderId },
        });
      }
    };

    useImperativeHandle(ref, () => ({
      callHandleClick: () => handleOnSubmit(),
    }));

    const checkPastAmount = async () => {
      try {
        let payload = {
          accounts_id: userData?.data?.accountData?.id,
          payment_method: "ACH",
          amount: amount,
        };
        let res: any = await dispatch(duplicatePaymentGet(payload)).unwrap();
        const { isDuplicateOrders, duplicateOrdersCount } = res?.body?.data;
        if (isDuplicateOrders && duplicateOrdersCount > 0) {
          return false;
        } else {
          return true;
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handleCheckDisclosureAndDuplicate = async () => {
      try {
        let moveAhead = !checkedDuplicatePayment
          ? await checkPastAmount()
          : true;
        if (!moveAhead) {
          setIsLoading(false);
          dispatch(onSetFinalDisclosureLoading(false));
          handleClick();
          return false;
        } else {
          if (
            (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
              portalSettings?.payment_agreement_opt ===
                SettingsValue.DEFAULT) &&
            !showFinalDisclosure
          ) {
            setIsLoading(false);
            dispatch(resetDuplicateOrder());
            dispatch(onSetFinalDisclosureLoading(false));
            dispatch(onSetShowDisclosure(true));
            handleClick();
          } else {
            dispatch(resetDuplicateOrder());
            dispatch(resetCheckDuplicatePayment());
            return true;
          }
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const createAddressPayload = (): AddressPayload => {
      let payload: AddressPayload = {};
      let data = countryAddressList()[values?.x_country || "US"];
      const arr = Object.keys(data).map((field) => field);

      payload.country = values?.x_country || "US";
      if (portalSettings?.cc_billing_address === SettingsValue.YES) {
        if (arr) {
          if (arr.includes("Address line 1")) {
            payload.line1 = values?.x_address || undefined;
          }
          if (arr.includes("Address line 2")) {
            payload.line2 = values?.address2 || undefined;
          }
          if (arr.includes("City") || arr.includes("Area")) {
            payload.city = values?.x_city || undefined;
          }
          if (
            arr.includes("CEDEX") ||
            arr.includes("ZIP") ||
            arr.includes("Eircode") ||
            arr.includes("PIN") ||
            arr.includes("Postal code")
          ) {
            payload.postal_code = values?.x_zip || undefined;
          }
          if (
            arr.includes("County") ||
            arr.includes("State") ||
            arr.includes("Department") ||
            arr.includes("District") ||
            arr.includes("Emirate") ||
            arr.includes("Island") ||
            arr.includes("Oblast") ||
            arr.includes("Parish") ||
            arr.includes("Prefecture") ||
            arr.includes("Province")
          ) {
            payload.state = values?.x_state || undefined;
          }
        }
      }
      return payload;
    };

    const createPaymentMethod = async ({
      cardElement,
      addressData,
    }: {
      cardElement: StripeCardNumberElement | null;
      addressData: AddressPayload;
    }) => {
      if (!stripe || !elements || isDisabled) {
        return;
      }

      if (!cardElement) return;

      const { paymentMethod, error: pmError } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: values.choldername,
            address: { ...addressData },
          },
        });

      if (pmError) {
        setCardErrMsg(pmError.message || "");
        return { success: false, error: pmError };
      } else {
        return { success: true, paymentMethod };
      }
    };

    const handleCardSetup = async ({
      clientSecret,
      orderId,
    }: {
      clientSecret: string;
      orderId?: number;
    }) => {
      if (!stripe || !elements || isDisabled) {
        setIsLoading(false);
        dispatch(setIsPaymentProcessing(false));
        return;
      }
      let stripeConfirmationSucceeded = false;
      try {
        const { error, setupIntent } =
          await stripe.confirmCardSetup(clientSecret);

        if (error) {
          await markIntentFailed(orderId, error);
          await verifyStripeCardPayment({
            intent: null,
            email,
            error,
            orderId,
            updateMethod: isUpdateAndPay,
          });
          setCardErrMsg(error.message || "");
          setIsLoading(false);
          await dispatch(onSetShowDisclosure(false));
          await dispatch(onSetFinalDisclosureLoading(false));
          return;
        } else {
          stripeConfirmationSucceeded = true;
          await verifyStripeCardPayment({
            intent: setupIntent?.id || null,
            email,
            error: null,
            orderId,
            updateMethod: isUpdateAndPay,
          });
          clearStoredCardElement();
        }
      } catch (error) {
        if (!stripeConfirmationSucceeded) {
          await markIntentFailed(orderId, error);
        }
        await dispatch(onSetShowDisclosure(false));
        await dispatch(onSetFinalDisclosureLoading(false));
        setCardErrMsg(PAYMENT_CONFIRMATION_ERROR);
        captureException(error, {
          tags: { payment_flow: "failed_payment_card_setup" },
        });
      }
    };

    const handleCardPayment = async ({
      clientSecret,
      orderId,
    }: {
      clientSecret: string;
      orderId?: number;
    }) => {
      if (!stripe || !elements || isDisabled) {
        setIsLoading(false);
        dispatch(setIsPaymentProcessing(false));
        return;
      }
      let stripeConfirmationSucceeded = false;
      try {
        const { error, paymentIntent } =
          await stripe.confirmCardPayment(clientSecret);
        if (error) {
          await markIntentFailed(orderId, error);
          await verifyStripeCardPayment({
            intent: null,
            email,
            error,
            orderId,
            updateMethod: isUpdateAndPay,
          });
          setCardErrMsg(error.message || "");
          await dispatch(onSetShowDisclosure(false));
          await dispatch(onSetFinalDisclosureLoading(false));
          setIsLoading(false);
          return;
        } else {
          stripeConfirmationSucceeded = true;
          await verifyStripeCardPayment({
            intent: paymentIntent?.id || null,
            email,
            error: null,
            orderId,
            updateMethod: isUpdateAndPay,
          });
          clearStoredCardElement();
        }
      } catch (error) {
        if (!stripeConfirmationSucceeded) {
          await markIntentFailed(orderId, error);
        }
        setCardErrMsg(PAYMENT_CONFIRMATION_ERROR);
        captureException(error, {
          tags: { payment_flow: "failed_payment_card_confirmation" },
        });
        await dispatch(onSetFinalDisclosureLoading(false));
        await dispatch(onSetShowDisclosure(false));
      }
    };

    const handleOnSubmit = async () => {
      setIsLoading(true);
      setCardErrMsg("");
      let moveAhead = await handleCheckDisclosureAndDuplicate();
      if (!moveAhead) {
        setIsLoading(false);
        return;
      }

      try {
        const cardElement = getStoredCardElement();
        const addressData = createAddressPayload();
        let paymentMethodResponse = await createPaymentMethod({
          cardElement,
          addressData,
        });

        if (!paymentMethodResponse?.success) {
          await dispatch(onSetShowDisclosure(false));
          await dispatch(onSetFinalDisclosureLoading(false));
          dispatch(setIsPaymentProcessing(false));
          setIsLoading(false);
          setCardErrMsg(
            paymentMethodResponse?.error?.message || PAYMENT_START_ERROR
          );
          return;
        }

        const payload = {
          total_amount: parseFloat(`${amount}` || "0.00") * 100,
          payment_method_types: "card",
          accountid: userData?.data?.accountData?.accountid || "",
          cluster_id: clusterId,
          isRecurring,
          description: "Online Payment",
          payment_note: "One-time",
          payment_method_id: paymentMethodResponse?.paymentMethod?.id || "",
          custom_conv: "on",
          isRetry: true,
          order_id: effectiveRetryOrderId,
        };

        const res: any = await dispatch(getIntent(payload)).unwrap();

        if (res?.body?.success) {
          const clientSecret = getClientSecret(res);

          if (!clientSecret) {
            setCardErrMsg(PAYMENT_START_ERROR);
            captureException(
              new Error("Payment intent response was missing client_secret"),
              {
                tags: { payment_flow: "failed_payment_card_intent" },
                extra: { response_success: true, is_recurring: isRecurring },
              }
            );
            await dispatch(onSetShowDisclosure(false));
            await dispatch(onSetFinalDisclosureLoading(false));
            dispatch(setIsPaymentProcessing(false));
            return;
          }

          if (isRecurring) {
            await handleCardSetup({
              clientSecret,
              orderId: getIntentOrderId(res),
            });
          } else {
            await handleCardPayment({
              clientSecret,
              orderId: getIntentOrderId(res),
            });
          }
        } else {
          setCardErrMsg(res?.body?.data?.message || PAYMENT_START_ERROR);
          await dispatch(onSetShowDisclosure(false));
          await dispatch(onSetFinalDisclosureLoading(false));
          dispatch(setIsPaymentProcessing(false));
        }
      } catch (error) {
        setCardErrMsg(PAYMENT_START_ERROR);
        captureException(error, {
          tags: { payment_flow: "failed_payment_card_intent" },
          extra: { is_recurring: isRecurring },
        });
        await dispatch(onSetShowDisclosure(false));
        await dispatch(onSetFinalDisclosureLoading(false));
        dispatch(setIsPaymentProcessing(false));
      } finally {
        setIsLoading(false);
        dispatch(setIsPaymentProcessing(false));
      }
    };

    return (
      <Button
        text={buttonText}
        className={className}
        {...testAttrs("card-payment-submit", buttonText)}
        onClick={handleOnSubmit}
        isLoading={isLoading}
        isDisabled={isDisabled}
      />
    );
  }
);

export default CardPaymentButton;
